import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs';

import { AuthPermission, AuthService } from './core/auth/auth.service';
import { ConfirmationModalComponent } from './core/confirmation/confirmation-modal.component';
import { LoadingOverlayService } from './core/loading/loading-overlay.service';

type NavigationItem = {
  label: string;
  path: string;
  icon:
    | 'dashboard'
    | 'tenders'
    | 'ai'
    | 'templates'
    | 'manage-sections'
    | 'eligibility'
    | 'compliance'
    | 'submitted-bids'
    | 'pre-bid-queries'
    | 'evaluation'
    | 'workflow'
    | 'reports'
    | 'documents'
    | 'clarifications'
    | 'users';
  badge?: string;
  permissionModules?: string[];
};

type NavigationSection = {
  id: string;
  label: string;
  items: NavigationItem[];
};

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, RouterOutlet, ConfirmationModalComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  private readonly authService = inject(AuthService);
  readonly loadingOverlayService = inject(LoadingOverlayService);
  private readonly router = inject(Router);
  readonly sidebarCollapsed = signal(false);
  readonly collapsedNavigationSections = signal<Record<string, boolean>>({});
  private readonly allNavigationSections: NavigationSection[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      items: [
        { label: 'Dashboard', path: '/dashboard', icon: 'dashboard' },
      ],
    },
    {
      id: 'tender-setup',
      label: 'Tender Setup',
      items: [
        // { label: 'Templates', path: '/templates', icon: 'templates', permissionModules: ['Templates'] },
        { label: 'Manage Sections', path: '/manage-sections', icon: 'manage-sections', permissionModules: ['Templates'] },
        { label: 'Templates Builder', path: '/template-builder', icon: 'templates', permissionModules: ['Templates'] },
        { label: 'AI Drafting', path: '/ai-drafting', icon: 'ai', permissionModules: ['AI Drafting'] },
        { label: 'Tender Management', path: '/tenders', icon: 'tenders', permissionModules: ['Tenders'] },
      ],
    },
    {
      id: 'tender-management',
      label: 'Tender Management',
      items: [
        { label: 'Document Management', path: '/documents', icon: 'documents', permissionModules: ['Documents'] },
        // { label: 'Eligibility Builder', path: '/eligibility', icon: 'eligibility', permissionModules: ['Eligibility Builder'] },
        { label: 'Compliance Engine', path: '/compliance', icon: 'compliance', permissionModules: ['Compliance Engine'] },
        { label: 'Pre-Bid Query Chat', path: '/pre-bid-queries', icon: 'pre-bid-queries', permissionModules: ['Pre-Bid Queries'] },
        { label: 'Submitted Bids', path: '/submitted-bids', icon: 'submitted-bids', permissionModules: ['Submitted Bids'] },
      ],
    },
    {
      id: 'evaluation',
      label: 'Evaluation',
      items: [
        {
          label: 'Pre-Qualification Evaluation',
          path: '/pre-qualification-evaluation',
          icon: 'evaluation',
          permissionModules: ['Evaluation'],
        },
        {
          label: 'EMD Evaluation',
          path: '/emd-evaluation',
          icon: 'evaluation',
          permissionModules: ['Evaluation'],
        },
        { label: 'Technical Evaluation', path: '/technical-evaluation', icon: 'evaluation', permissionModules: ['Evaluation'] },
        { label: 'Financial Evaluation', path: '/financial-evaluation', icon: 'evaluation', permissionModules: ['Evaluation'] },
        { label: 'Bid Evaluation', path: '/evaluation', icon: 'evaluation', permissionModules: ['Evaluation'] },
        { label: 'Clarifications', path: '/clarifications', icon: 'clarifications', permissionModules: ['Submitted Bids'] },
      ],
    },
    {
      id: 'workflow',
      label: 'Workflow',
      items: [
        { label: 'Workflow & Approval', path: '/workflow', icon: 'workflow', permissionModules: ['Workflow'] },
        { label: 'Workflow Configuration', path: '/workflow-config', icon: 'workflow', permissionModules: ['Workflow Configuration'] },
      ],
    },
    {
      id: 'reports-analytics',
      label: 'Reports & Analytics',
      items: [
        { label: 'Reports & Analytics', path: '/reports', icon: 'reports', permissionModules: ['Reports'] },
      ],
    },
    {
      id: 'administration',
      label: 'Administration',
      items: [
        {
          label: 'User Management',
          path: '/users',
          icon: 'users',
          permissionModules: ['Users', 'Roles', 'Departments', 'Designations', 'Logs'],
        },
      ],
    },
  ];

  readonly isLoginRoute = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => this.isLoginUrl(event.urlAfterRedirects)),
      startWith(this.isLoginUrl(this.router.url)),
    ),
    { initialValue: this.isLoginUrl(this.router.url) },
  );

  readonly profileName = computed(() => this.authService.session()?.username ?? 'Admin');
  readonly profileRole = computed(() => this.authService.session()?.role ?? 'Admin');
  readonly profileInitial = computed(() => this.profileName().trim().charAt(0).toUpperCase() || 'A');
  readonly navigationSections = computed(() => {
    const permissions = this.authService.session()?.permissions ?? [];

    return this.allNavigationSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => this.canShowNavigationItem(item, permissions)),
      }))
      .filter((section) => section.items.length > 0);
  });

  canCollapseSection(section: NavigationSection): boolean {
    return section.items.length > 1;
  }

  isSectionCollapsed(sectionId: string): boolean {
    return this.collapsedNavigationSections()[sectionId] ?? false;
  }

  toggleSection(sectionId: string): void {
    this.collapsedNavigationSections.update((current) => ({
      ...current,
      [sectionId]: !(current[sectionId] ?? false),
    }));
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update((value) => !value);
  }

  logout(): void {
    this.authService.logout().subscribe(() => {
      void this.router.navigate(['/login']);
    });
  }

  private isLoginUrl(url: string): boolean {
    return url === '/login' || url.startsWith('/login?');
  }

  private canShowNavigationItem(item: NavigationItem, permissions: AuthPermission[]): boolean {
    if (!item.permissionModules?.length) {
      return true;
    }

    const allowedModules = new Set(permissions.map((permission) => permission.module));
    return item.permissionModules.some((module) => allowedModules.has(module));
  }
}
